import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useFm, useToast } from '@web3/base-hooks';
import { Modal } from '@bfe/moly';
import { getBbSolUserAirdropRewards } from '@/services/bbysol';
import { AirdropReward } from '@/types/bbsol';
import { useWallet } from '@/hooks/wallet';
import { useStakingStore } from '@/store/staking';
import { initialWindowType } from '@/hooks/media';
import { scTrack } from '@web3/base-tracking';
import { Event } from '@/config/track-config';
import { usePool } from '@/hooks/useAirdrop';
import { formatNumber } from '@web3/base-utils';

import styles from './index.module.scss';

export function useAirdropReward({ walletAddress, activityCode }) {
  const [reward, setReward] = useState<AirdropReward | null>();
  const { toastWarn } = useToast();
  // 获取数据
  const fetchData = async () => {
    const { result, success, message } = await getBbSolUserAirdropRewards(
      walletAddress,
      activityCode
    );
    if (!success) {
      toastWarn(message);
      return;
    }
    if (!result) {
      toastWarn('no airdrop activity');
      return;
    }
    setReward(result);
  };
  //
  //
  useEffect(() => {
    if (!walletAddress) return;
    if (!activityCode) return;
    fetchData();
  }, [activityCode, walletAddress]);
  //
  return {
    reward,
    walletAddress,
    activityCode,
  };
}

export default function Rewards({ walletAddress, reward, activity }) {
  const fm = useFm();
  //
  const noWalletAddressItems = Array.from(Array(10).keys()).map((i) => ({
    snapshotTime: '--',
    amount: '--',
    rewards: '--',
  }));
  const items = walletAddress ? reward?.list || [] : noWalletAddressItems;
  //
  return (
    <>
      <div
        className={`grid break-all grid-cols-3 p-4 gap-4 text-sm sm:rounded-t-lg ${styles['bg-table-header']}`}
      >
        <div>{fm('snapshotDatesTime')}*</div>
        <div>{fm('snapshotAmtBBSOL')}*</div>
        <div className="sm:whitespace-nowrap">
          {fm('estimatedRewards', {
            tokenSymbol: activity?.rewardTokenDetail?.symbol,
          })}
          *
        </div>
      </div>

      <div className="border rounded-b border-t-none border-bds-gray-ele-border">
        <div
          className={`${styles['display-scrollbar']} overflow-y-scroll max-h-[30vh] sm:max-h-[40vh]`}
        >
          {items.map((item) => (
            <div
              key={item.snapshotTime}
              className="grid p-4 grid-cols-3 text-xs gap-4 border-b border-b-bds-gray-ele-border"
            >
              <div>
                {item.snapshotTime && item.snapshotTime !== '--'
                  ? `${dayjs.unix(item.snapshotTime).utc().format('D MMMM, YYYY, h:mm')} UTC`
                  : '--'}
              </div>
              <div>
                {formatNumber(item.amount || '0', {
                  kmb: false,
                  decimal: 4,
                  roundMode: 1,
                  smallNumberUseValidDigits: false,
                  collapseZero: false,
                }).replace(/,/g, '')}
              </div>
              <div>
                {formatNumber(item.rewards || '0', {
                  kmb: false,
                  decimal: 1,
                  roundMode: 1,
                  smallNumberUseValidDigits: false,
                  collapseZero: false,
                }).replace(/,/g, '')}
              </div>
            </div>
          ))}
        </div>

        <div className="grid p-4 gap-4 grid-cols-3 text-xs">
          <div>{fm('total')}*</div>
          <div>--</div>
          <div>
            {(reward?.estimatedTotalRewards || 0) > 0
              ? formatNumber(reward.estimatedTotalRewards || '0', {
                  kmb: false,
                  decimal: 1,
                  roundMode: 1,
                  smallNumberUseValidDigits: false,
                  collapseZero: false,
                }).replace(/,/g, '')
              : '--'}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * rewards modal，获取钱包和 reward 数据都放到了这个 modal
 * @param activity 活动数据
 * @returns
 */
export function RewardsModal({ activity }) {
  const fm = useFm();
  const { isLogin, solanaWalletInfo } = useWallet();
  const { airdrop, setAirdrop } = useStakingStore();
  const { isMobile } = initialWindowType;
  const poolHooks = usePool();
  const airdropReward = useAirdropReward({
    walletAddress: solanaWalletInfo?.address,
    activityCode: activity?.activityCode,
  });
  //
  const closeModal = () => setAirdrop({ isRewardsOpened: false });
  //
  if (!airdrop.isRewardsOpened) {
    return null;
  }
  //
  return (
    <Modal
      isMobile={isMobile}
      titleCenter={isMobile}
      closable={!isMobile}
      onClose={() => closeModal()}
      zIndex={9}
      size="small"
      direction={!isMobile ? 'center' : 'bottom'}
      title={fm('myAirdropModalTitle', {
        tokenSymbol: activity?.rewardTokenDetail?.symbol,
      })}
      width={isMobile ? '100%' : 800}
      open={airdrop.isRewardsOpened}
      footer={
        <div className="flex -mx-2 sm:mx-0 text-center sm:gap-6 gap-4 mt-6 items-center justify-center flex-col sm:flex-row">
          {isLogin && (
            <button
              type="button"
              onClick={() => poolHooks.onJump()}
              className="min-w-48 w-full sm:w-auto font-semibold bg-bds-brand-primary hover:bg-bds-brand-500 text-white py-3 px-6 rounded-lg"
            >
              {fm('getMoreBbSOL')}
            </button>
          )}

          <Link
            className="min-w-48 w-full sm:w-auto font-semibold border hover:bg-bds-gradient border-bds-divide text-white py-3 px-6 rounded-lg"
            target="_blank"
            href={isMobile ? activity?.jumpLinkApp : activity?.jumpLinkWeb}
            onClick={() => {
              scTrack(Event.Web3UserClick, {
                page_name: 'StakingWeb_bbSOLPage',
                button_name: 'Airdrop_detail',
              });
            }}
          >
            {fm('airdropRule')}
          </Link>
          <button
            type="button"
            onClick={closeModal}
            className="sm:hidden min-w-48 w-full sm:w-auto font-semibold hover:bg-bds-gradient text-white py-3 px-6 rounded-lg"
          >
            {fm('cancel')}
          </button>
        </div>
      }
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <div className="-mx-6 sm:mx-0">
        <Rewards
          walletAddress={airdropReward?.walletAddress}
          reward={airdropReward?.reward}
          activity={activity}
        />
      </div>
    </Modal>
  );
}
